import React, { createContext, useCallback, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { SearchDialogAnalyticsContext } from '../../common/analytics';
import { useFilter, addFilters as addFiltersAction, updateFilterState, replaceAllFilters as replaceAllFiltersAction } from '../../common/filters/use-filters';
import { useSessionUserInput } from '../../extensible/user-input-provider';
export const getPreQueryLastModifiedDate = () => {
  const lastModifiedDate = new Date();
  lastModifiedDate.setDate(lastModifiedDate.getDate() - 90);
  return lastModifiedDate.toISOString().split('T')[0];
};
const useFilters = (defaultFilters = [], storeFilters = value => {}) => {
  const {
    filters,
    dispatch
  } = useFilter(defaultFilters);
  useLayoutEffect(() => {
    storeFilters(filters);
  });
  const addFilters = useCallback(newFilters => {
    dispatch(addFiltersAction(newFilters));
    storeFilters(filters.concat(newFilters));
  }, [dispatch, filters, storeFilters]);
  const updateFilter = useCallback((id, isChecked) => {
    dispatch(updateFilterState(id, isChecked, true));
    const index = filters.findIndex(filter => filter.id === id);
    const newFilters = [...filters];
    newFilters[index] = {
      ...newFilters[index],
      isChecked: isChecked,
      isVisible: true
    };
    storeFilters(filters.concat(newFilters));
  }, [dispatch, filters, storeFilters]);
  const clearFilter = useCallback(() => {
    filters.forEach(f => {
      dispatch(updateFilterState(f.id, false));
    });
    const newFilters = filters.map(f => {
      const newF = {
        ...f
      };
      newF.isChecked = false;
      return newF;
    });
    storeFilters(newFilters);
  }, [dispatch, filters, storeFilters]);
  const resetFilter = useCallback(() => {
    dispatch(replaceAllFiltersAction(defaultFilters));
  }, [defaultFilters, dispatch]);
  return {
    availableFilters: filters,
    addFilters,
    updateFilter,
    clearFilter,
    reset: resetFilter,
    dispatch
  };
};
const DEFAULT_FILTER_STATE = {
  availableFilters: [],
  addFilters: () => {},
  updateFilter: () => {},
  clearFilter: () => {},
  reset: () => {},
  dispatch: () => {}
};
const FilterContext = /*#__PURE__*/createContext({
  spaceFilters: DEFAULT_FILTER_STATE,
  peopleFilters: DEFAULT_FILTER_STATE,
  siteFilters: DEFAULT_FILTER_STATE,
  resetFilters: () => {},
  prequeryFiltersRef: {
    current: ''
  }
});
export function transformFiltersForAnalytics(availableFilters) {
  const appliedFilters = availableFilters.map((availableFilter, index) => ({
    ...availableFilter,
    index
  })).filter(filterWithIndex => filterWithIndex.isChecked);
  return {
    applied: appliedFilters.map(appliedFilter => ({
      id: appliedFilter.id,
      source: appliedFilter.filterSource,
      index: appliedFilter.index
    })),
    recommendedIds: availableFilters.map(appliedFilter => ({
      id: appliedFilter.id,
      source: appliedFilter.filterSource
    }))
  };
}
export const FilterContextProvider = ({
  isEnabled = true,
  defaultSpaceFilters,
  defaultPeopleFilters,
  defaultSiteFilters,
  children
}) => {
  const previousEnabledState = React.useRef(isEnabled);
  const {
    stickySearchEnabled,
    readFilters,
    storeFilters,
    resetConfluenceFiltersRef
  } = useSessionUserInput();
  const [filters, setFilters] = useState(readFilters('confluence'));
  const initialSpaceFilters = stickySearchEnabled ? (filters === null || filters === void 0 ? void 0 : filters.space) || defaultSpaceFilters : defaultSpaceFilters;
  const initialPeopleFilters = stickySearchEnabled ? (filters === null || filters === void 0 ? void 0 : filters.people) || defaultPeopleFilters : defaultPeopleFilters;
  const initialSiteFilters = stickySearchEnabled ? (filters === null || filters === void 0 ? void 0 : filters.site) || defaultSiteFilters : defaultSiteFilters;
  const getUpdateFilters = useCallback(type => options => {
    const filters = readFilters('confluence');
    switch (type) {
      case 'SPACE':
        filters.space = options;
        break;
      case 'PEOPLE':
        filters.people = options;
        break;
      case 'SITE':
        filters.site = options;
        break;
    }
    storeFilters('confluence', filters);
  }, [readFilters, storeFilters]);
  const spaceFilterStore = useFilters(initialSpaceFilters, stickySearchEnabled ? getUpdateFilters('SPACE') : undefined);
  const peopleFilterStore = useFilters(initialPeopleFilters, stickySearchEnabled ? getUpdateFilters('PEOPLE') : undefined);
  const siteFilterStore = useFilters(initialSiteFilters, stickySearchEnabled ? getUpdateFilters('SITE') : undefined);
  const prequeryFiltersRef = useRef('');
  const filtersAnalyticsContext = spaceFilterStore.availableFilters.length === 0 && peopleFilterStore.availableFilters.length === 0 ? {} : {
    container: transformFiltersForAnalytics(spaceFilterStore.availableFilters),
    contributor: transformFiltersForAnalytics(peopleFilterStore.availableFilters)
  };
  const resetFilters = useCallback(() => {
    spaceFilterStore.dispatch(replaceAllFiltersAction(defaultSpaceFilters || []));
    peopleFilterStore.dispatch(replaceAllFiltersAction(defaultPeopleFilters || []));
    siteFilterStore.dispatch(replaceAllFiltersAction(defaultSiteFilters || []));
  }, [defaultPeopleFilters, defaultSiteFilters, defaultSpaceFilters, peopleFilterStore, siteFilterStore, spaceFilterStore]);
  const clearFilters = useCallback(() => {
    spaceFilterStore.clearFilter();
    peopleFilterStore.clearFilter();
    siteFilterStore.clearFilter();
    if (stickySearchEnabled) {
      setFilters(readFilters('confluence'));
    }
  }, [spaceFilterStore, peopleFilterStore, siteFilterStore, stickySearchEnabled, readFilters]);
  useEffect(() => {
    if (resetConfluenceFiltersRef) {
      resetConfluenceFiltersRef.current = clearFilters;
    }
  }, [clearFilters, resetConfluenceFiltersRef]);

  // We reset the state when the component becomes disabled
  if (previousEnabledState.current && !isEnabled && !stickySearchEnabled) {
    resetFilters();
  }
  previousEnabledState.current = isEnabled;
  return /*#__PURE__*/React.createElement(FilterContext.Provider, {
    value: {
      spaceFilters: spaceFilterStore,
      peopleFilters: peopleFilterStore,
      siteFilters: siteFilterStore,
      resetFilters,
      prequeryFiltersRef
    }
  }, /*#__PURE__*/React.createElement(SearchDialogAnalyticsContext, {
    analyticContext: {},
    nonPrivacySafeAnalyticContext: {},
    filterAnalyticsContext: filtersAnalyticsContext
  }, children));
};
export const useFilterContext = () => {
  return React.useContext(FilterContext);
};